// src/oura/newscan/fixtures/journey.ts
//
// The seam between the two sample stores (JOURNEY-SPEC v1.1 §1 stages 4 to 7).
// The scan draft fixture store must read a signed client's facts (the form,
// the quote, the plan) to draft its First AI check, and must tell the
// onboarding store when that check starts. The onboarding store
// (src/services/aeo/onboarding.ts) registers its side here at load; the draft
// store calls it. Neither store imports the other, so there is no cycle.
//
// Fixtures only. On the live backend the server does both: the form-filing job
// builds the draft (O1) and the draft start writes `first_assessment_id` (H4-a).

import type { BuyMode } from "@service/aeo/scanDraftTypes";

/** What the onboarding record says about a signed client, as the draft needs it. */
export interface SignedFacts {
    businessId: string;
    domain: string;
    businessName: string;
    /** The form's contact, else the record's. */
    contactName: string;
    /** ISO time the form was answered. Null: not yet, so no first_check draft. */
    answeredAt: string | null;
    /** The form as parse_answers files it (BE oura_onboarding.py:1113): goals a list, plans one text. */
    form: { services: string[]; areas: string[]; customerQuestions: string[]; goals: string[]; plans: string; other: string } | null;
    quote: { ref: string; outlets: number; areas: string[] } | null;
    /** Priority keywords the plan already holds. */
    heldKeywords: string[];
    /** Outlet names the plan already holds. */
    heldOutletNames: string[];
    /** `onboarding.buy_mode`. Null: not known. */
    buyMode: BuyMode | null;
    /** The record's first AI check (`first_assessment_id` and its run). */
    firstCheck: { status: "not_started" | "running" | "complete" | "failed"; assessmentId: string | null; finishedAt?: string | null };
}

/**
 * One clock for every sample scan, pitch and First AI check alike: a sample
 * scan finishes this long after it starts. Two clocks let one business read
 * "Scanning" on the list, a finished check on Onboarding and "Not yet scored"
 * on Delivery at once (Angel, 15 Oct). Live, the scan job's own status is the
 * one source for all three.
 */
export const SAMPLE_SCAN_MS = 3 * 60_000;

/** A pitch (a prospect's New scan) the sample clock has finished. */
export interface PitchRunDone {
    domain: string;
    businessName: string;
    category: string;
    city: string;
    assessmentId: string;
    startedAt: string;
    finishedAt: string;
}

/** The first AI check a first_check draft started, for the onboarding record. */
export interface FirstCheckRun {
    assessmentId: string;
    /**
     * When the run started. Set when the first check takes over the pitch scan
     * still running on the same 50 ("Use the pitch scan"): it finishes when
     * the pitch does. Absent: now.
     */
    startedAt?: string;
    /** The approved outlets, countries dropped (`onboarding.outlet_areas`). */
    outletAreas: string[];
    /** "For the plan" answers: null leaves the plan's own. */
    priorityKeywords: string[] | null;
    outletNames: string[] | null;
}

export interface OnboardingSide {
    facts(businessId: string): SignedFacts | null;
    firstCheckStarted(businessId: string, run: FirstCheckRun): void;
    /** A pitch finished: its business row reads the scored scan (a prospect, or a client signed while it ran). */
    pitchFinished(run: PitchRunDone): void;
}

let side: OnboardingSide | null = null;

/** The onboarding sample store, at its load. A second call replaces the first (hot reload). */
export function registerOnboardingSide(next: OnboardingSide | null): void {
    side = next;
}

/** Null when no onboarding sample store is loaded (a test, or onboarding live). */
export function onboardingSide(): OnboardingSide | null {
    return side;
}
